import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spin, Alert, Typography, Table, Empty, Tag } from 'antd';
import { getPathologyAccuracy, getDashboardSummary, getDashboardAccuracy } from '../services/api';
import { useBranch } from '../components/BranchContext';

const { Text } = Typography;

const Insights = () => {
  const [pathologyData, setPathologyData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [accuracyData, setAccuracyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { branchParam } = useBranch();

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = branchParam ? { branch: branchParam } : {};
        const [pathRes, summRes, accRes] = await Promise.all([
          getPathologyAccuracy(false, params),
          getDashboardSummary(params),
          getDashboardAccuracy(params),
        ]);
        setPathologyData(pathRes.pathologies || pathRes.data || []);
        setSummaryData(summRes);
        setAccuracyData(accRes);
      } catch (err) {
        setError(err.message || 'Failed to load insights data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [branchParam]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading insights...</div>
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message="Error" description={error} showIcon />;
  }

  const topMissed = [...pathologyData]
    .filter((d) => d.fn > 0)
    .sort((a, b) => b.fn - a.fn)
    .slice(0, 15);

  const topOvercalled = [...pathologyData]
    .filter((d) => d.fp > 0)
    .sort((a, b) => b.fp - a.fp)
    .slice(0, 15);

  const studyTypeAccuracy = (accuracyData?.by_study_type || [])
    .filter((s) => s.total > 0)
    .sort((a, b) => a.accuracy_pct - b.accuracy_pct);

  const byStudyType = (summaryData?.by_study_type || []).map((s) => {
    const c = s.classifications || {};
    const major = c['Major'] || 0;
    const majorRate = s.total > 0 ? ((major / s.total) * 100).toFixed(1) : 0;
    return { study_type: s.study_type, total: s.total, major, major_rate: parseFloat(majorRate) };
  }).sort((a, b) => b.major_rate - a.major_rate);

  const pathCols = [
    { title: 'Pathology', dataIndex: 'pathology', key: 'pathology', render: (t) => <Text strong style={{ textTransform: 'capitalize' }}>{t}</Text> },
    { title: 'Count', dataIndex: 'count', key: 'count', width: 80 },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="Top Missed Pathologies (False Negatives)" size="small" style={{ minHeight: 400 }}>
            {topMissed.length > 0 ? (
              <Table
                dataSource={topMissed.map(d => ({ pathology: d.pathology, count: d.fn }))}
                columns={[
                  ...pathCols,
                  { title: '', key: 'bar', render: (_, r) => (
                    <div style={{ background: '#E87722', height: 16, width: `${Math.max(8, (r.count / (topMissed[0]?.fn || 1)) * 100)}%`, borderRadius: 4 }} />
                  )}
                ]}
                rowKey="pathology"
                size="small"
                pagination={false}
              />
            ) : (
              <Empty description="No false negatives found" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Top Overcalled Pathologies (False Positives)" size="small" style={{ minHeight: 400 }}>
            {topOvercalled.length > 0 ? (
              <Table
                dataSource={topOvercalled.map(d => ({ pathology: d.pathology, count: d.fp }))}
                columns={[
                  ...pathCols,
                  { title: '', key: 'bar', render: (_, r) => (
                    <div style={{ background: '#D32F2F', height: 16, width: `${Math.max(8, (r.count / (topOvercalled[0]?.fp || 1)) * 100)}%`, borderRadius: 4 }} />
                  )}
                ]}
                rowKey="pathology"
                size="small"
                pagination={false}
              />
            ) : (
              <Empty description="No false positives found" />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Major Discrepancy Rate by Study Type" size="small">
            {byStudyType.length > 0 ? (
              <Table
                dataSource={byStudyType}
                columns={[
                  { title: 'Study Type', dataIndex: 'study_type', key: 'study_type', render: (t) => <Text strong>{t}</Text> },
                  { title: 'Total', dataIndex: 'total', key: 'total', width: 80 },
                  { title: 'Major', dataIndex: 'major', key: 'major', width: 80, render: (v) => <Text style={{ color: '#D32F2F' }}>{v}</Text> },
                  { title: 'Rate', dataIndex: 'major_rate', key: 'rate', width: 100, render: (v) => (
                    <Tag color={v >= 30 ? 'red' : v >= 15 ? 'orange' : 'green'}>{v}%</Tag>
                  )},
                ]}
                rowKey="study_type"
                size="small"
                pagination={false}
              />
            ) : (
              <Empty description="No data" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="AI Accuracy by Study Type (No Changes Rate)" size="small">
            {studyTypeAccuracy.length > 0 ? (
              <Table
                dataSource={studyTypeAccuracy}
                columns={[
                  { title: 'Study Type', dataIndex: 'study_type', key: 'study_type', render: (t) => <Text strong>{t}</Text> },
                  { title: 'Total', dataIndex: 'total', key: 'total', width: 80 },
                  { title: 'Accuracy', dataIndex: 'accuracy_pct', key: 'acc', width: 120, render: (v) => (
                    <Tag color={v >= 50 ? 'green' : v >= 30 ? 'orange' : 'red'}>{v?.toFixed(1)}%</Tag>
                  )},
                ]}
                rowKey="study_type"
                size="small"
                pagination={false}
              />
            ) : (
              <Empty description="No data" />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Insights;
